import { useContext, useEffect } from 'react';

import AuthContext from '../../context/authContext';
import { useAttention } from '../../context/attentionContext';
import useCustomerExperienceFeedback from '../../hooks/useCustomerExperienceFeedback';

// Poll the overdue endpoint every 5 minutes. The pop-up cadence itself is
// controlled by the hour-bucket in the attention key (see below), not by this
// interval, so a shorter poll only makes newly-overdue items appear promptly.
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const FEEDBACK_KEY_PREFIX = 'feedbackOverdue:';
const HOUR_MS = 60 * 60 * 1000;

// Globally mounted watcher: regardless of the active view, it polls for negative
// customer feedback that is still unanswered past its 24h response deadline and
// raises ONE collected attention pop-up summarising all overdue items (not one
// pop-up per item). To "bombard" the responsible staff exactly once per hour, the
// attention key embeds the current hour bucket: each new hour yields a fresh key,
// so as long as anything is still overdue the collected reminder re-pops once
// every hour until everything is answered. Mounted once in RootLayout so the
// pop-up can appear anywhere in the app.
const FeedbackOverdueWatcher = () => {
  const authCtx = useContext(AuthContext);
  const { getOverdueFeedback } = useCustomerExperienceFeedback();
  const { raiseAttention, releaseAttention } = useAttention();

  const token = authCtx?.token;
  const canRead =
    authCtx?.checkRight?.('customerExperienceFeedback:read') ||
    authCtx?.checkRight?.('customerExperienceFeedback:read:own');
  // These reminders are meant for company/office devices only. Suppress them on
  // POS terminals (deviceType 'counter') so they never interrupt checkout, and
  // on remembered private devices.
  const suppressedDevice = Boolean(authCtx?.isCounterDevice || authCtx?.isPrivateDevice);

  useEffect(() => {
    if (!token || !canRead || suppressedDevice) {
      // Drop any feedback alerts still queued from a previous session/scope.
      releaseAttention(key => key.startsWith(FEEDBACK_KEY_PREFIX));
      return undefined;
    }

    let cancelled = false;

    const check = async () => {
      let response;
      try {
        response = await getOverdueFeedback();
      } catch (_) {
        // Background watcher stays silent; the feedback view surfaces errors.
        return;
      }
      if (cancelled) return;

      const items = Array.isArray(response?.items) ? response.items : [];
      // One shared hour bucket so the collected reminder re-pops once per hour.
      const hourBucket = Math.floor(Date.now() / HOUR_MS);

      if (items.length === 0) {
        // Nothing overdue anymore: drop any queued reminder so it can't linger.
        releaseAttention(key => key.startsWith(FEEDBACK_KEY_PREFIX));
        return;
      }

      const count = items.length;

      // The pop-up only states THAT overdue feedback has to be answered. It lists
      // neither customer names nor booking numbers — staff open the feedback view
      // to see which items are affected.

      // Single collected pop-up. The hour bucket in the key makes it re-pop once
      // per hour while anything is still overdue; releasing stale buckets keeps
      // the seen-set bounded.
      releaseAttention(
        key => key.startsWith(FEEDBACK_KEY_PREFIX) && key !== `${FEEDBACK_KEY_PREFIX}${hourBucket}`
      );
      raiseAttention({
        key: `${FEEDBACK_KEY_PREFIX}${hourBucket}`,
        title:
          count === 1
            ? '1 Kundenfeedback überfällig'
            : `${count} Kundenfeedbacks überfällig`,
        message:
          count === 1
            ? 'Ein negatives Kundenfeedback ist seit über 24 Stunden unbeantwortet und muss jetzt beantwortet werden.'
            : `${count} negative Kundenfeedbacks sind seit über 24 Stunden unbeantwortet und müssen jetzt beantwortet werden.`,
        severity: 'error',
        primaryAction: { label: 'Zum Feedback', to: '/customer-experience-feedback' },
      });
    };

    check();
    const interval = setInterval(check, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // getOverdueFeedback/raiseAttention/releaseAttention are stable enough; we
    // key the watcher lifecycle on auth state and the device gate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, canRead, suppressedDevice]);

  return null;
};

export default FeedbackOverdueWatcher;
